import { Component, OnInit } from '@angular/core';
import { CodeService } from '../../services/code.service';

@Component({
  selector: 'app-code-import',
  templateUrl: './code-import.component.html',
  styleUrls: ['./code-import.component.css']
})
export class CodeImportComponent implements OnInit {
  // Step tracking
  uploadStep: number = 1;  // 1: Select & Map, 2: Preview, 3: Results

  // File upload state
  csvFile: File;
  csvFilename: string = '';
  fileError: string = '';
  csvContent: string = '';

  // CSV data
  csvRows: string[][] = [];
  csvHeaders: string[] = [];
  dbFields: string[] = [
    'code_name', 'code_title', 'code_category', 'code_finding',
    'code_recommendation', 'code_notes', 'code_special_pricing_notes',
    'code_uses_chemicals'
  ];

  // Display names for database fields (user-friendly)
  fieldDisplayNames: { [key: string]: string } = {
    'code_name': 'Code',
    'code_title': 'Title',
    'code_category': 'Category',
    'code_finding': 'Finding',
    'code_recommendation': 'Recommendation',
    'code_notes': 'Notes',
    'code_special_pricing_notes': 'Pricing Notes',
    'code_uses_chemicals': 'Uses Chemicals'
  };

  // Field mapping
  fieldMapping: { [csvField: string]: string } = {};
  suggestedMapping: { [csvField: string]: string } = {};
  mappingError: string = '';

  // Preview & Results
  previewData: any[] = [];
  mappedFields: Array<{ csvField: string; dbField: string }> = [];
  importResults: any = { imported: 0, updated: 0, errors: [] };
  isLoading: boolean = false;

  constructor(private codeService: CodeService) { }

  ngOnInit(): void {
  }

  /**
   * Handle file selection - read file locally using FileReader
   */
  onFileSelect(event: any): void {
    this.fileError = '';
    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];

      // Validate file type
      if (!file.name.match(/\.(csv|txt)$/i)) {
        this.fileError = 'Please select a valid CSV or TXT file';
        return;
      }

      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        this.fileError = 'File size exceeds 10MB limit';
        return;
      }

      this.csvFile = file;
      this.csvFilename = file.name;

      // Read file locally using FileReader
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          this.csvContent = e.target.result;
          this.parseCSV(this.csvContent);
          this.generateSuggestedMapping();
        } catch (error) {
          this.fileError = 'Error reading file: ' + error.message;
        }
      };
      reader.onerror = () => {
        this.fileError = 'Error reading file';
      };
      reader.readAsText(file);
    }
  }

  /**
   * Parse CSV content into rows and headers, properly handling multiline quoted fields
   * Follows RFC 4180 CSV standard:
   * - Quoted fields can contain commas, newlines, and quotes (escaped as "")
   * - Unquoted fields are trimmed
   * - Quoted fields preserve internal spacing
   */
  private parseCSV(content: string): void {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentField = '';
    let insideQuotes = false;
    let fieldWasQuoted = false;
    const length = content.length;
    let i = 0;

    while (i < length) {
      const char = content[i];

      if (!insideQuotes && char === '"') {
        // Opening quote for this field
        fieldWasQuoted = true;
        insideQuotes = true;
        i++;
      } else if (insideQuotes && char === '"') {
        // Could be closing quote or escaped quote
        if (content[i + 1] === '"') {
          // Escaped quote - add one quote to field
          currentField += '"';
          i += 2;
        } else {
          // Closing quote
          insideQuotes = false;
          i++;
        }
      } else if (char === ',' && !insideQuotes) {
        // Field separator - save current field
        currentRow.push(fieldWasQuoted ? currentField : currentField.trim());
        currentField = '';
        fieldWasQuoted = false;
        i++;
      } else if ((char === '\n' || char === '\r') && !insideQuotes) {
        // Row separator - save current field and row
        if (currentField !== '' || currentRow.length > 0) {
          currentRow.push(fieldWasQuoted ? currentField : currentField.trim());
          currentField = '';
          fieldWasQuoted = false;

          if (currentRow.length > 0) {
            rows.push(currentRow);
            currentRow = [];
          }
        }
        // Skip \r\n
        if (char === '\r' && content[i + 1] === '\n') {
          i += 2;
        } else {
          i++;
        }
      } else {
        currentField += char;
        i++;
      }
    }

    // Add last field and row if exists
    if (currentField !== '' || currentRow.length > 0) {
      currentRow.push(fieldWasQuoted ? currentField : currentField.trim());
    }
    if (currentRow.length > 0) {
      rows.push(currentRow);
    }

    this.csvRows = rows;

    // First row is headers
    if (this.csvRows.length > 0) {
      this.csvHeaders = this.csvRows[0];
      this.csvRows = this.csvRows.slice(1); // Remove header row
    }
  }


  /**
   * Auto-detect field mappings based on CSV headers
   */
  private generateSuggestedMapping(): void {
    this.suggestedMapping = {};
    const headerLower = this.csvHeaders.map(h => h.toLowerCase());

    for (let i = 0; i < this.csvHeaders.length; i++) {
      const lowerHeader = headerLower[i];
      let dbField = null;

      // Match CSV header to database field
      if (['code', 'millenium code'].includes(lowerHeader)) {
        dbField = 'code_name';
      } else if (lowerHeader === 'title') {
        dbField = 'code_title';
      } else if (lowerHeader === 'category') {
        dbField = 'code_category';
      } else if (lowerHeader === 'finding') {
        dbField = 'code_finding';
      } else if (lowerHeader === 'recommendation') {
        dbField = 'code_recommendation';
      } else if (lowerHeader === 'notes') {
        dbField = 'code_notes';
      } else if (['pricing notes', 'special pricing notes'].includes(lowerHeader)) {
        dbField = 'code_special_pricing_notes';
      } else if (['uses chems', 'uses chemicals'].includes(lowerHeader)) {
        dbField = 'code_uses_chemicals';
      }

      if (dbField) {
        this.suggestedMapping[this.csvHeaders[i]] = dbField;
      }
    }

    // Apply suggested mapping
    this.fieldMapping = { ...this.suggestedMapping };
  }

  /**
   * Move to step 2 after file is selected and parsed
   */
  proceedToPreview(): void {
    if (!this.csvHeaders || this.csvHeaders.length === 0) {
      this.fileError = 'No headers found in CSV file';
      return;
    }

    if (!Object.values(this.fieldMapping).includes('code_name')) {
      this.mappingError = 'code_name field must be mapped';
      return;
    }

    // Generate preview data locally
    this.generatePreviewData();
    this.uploadStep = 2;
  }

  /**
   * Handle field mapping change
   */
  onMappingChange(csvField: string, dbField: string): void {
    this.mappingError = '';

    if (dbField === 'skip') {
      delete this.fieldMapping[csvField];
    } else {
      this.fieldMapping[csvField] = dbField;
    }

    // Validate code_name is mapped
    if (!Object.values(this.fieldMapping).includes('code_name')) {
      this.mappingError = 'code_name field must be mapped to proceed';
    }
  }

  /**
   * Generate preview data locally from CSV content
   */
  private generatePreviewData(): void {
    this.previewData = [];
    this.mappedFields = [];

    // Build mapped fields list
    for (const csvHeader of this.csvHeaders) {
      if (this.fieldMapping[csvHeader]) {
        this.mappedFields.push({
          csvField: csvHeader,
          dbField: this.fieldMapping[csvHeader]
        });
      }
    }

    const rowsToShow = this.csvRows.slice(0, 10); // First 10 rows

    for (const row of rowsToShow) {
      const mappedRow = this.mapCsvRow(row);
      if (mappedRow) {
        this.previewData.push(mappedRow);
      }
    }
  }

  /**
   * Map a single CSV row using field mapping
   * Maps from CSV columns to database fields based on the field mapping
   */
  private mapCsvRow(row: string[]): any {
    const mapped: any = {};

    // Get all the CSV headers that have been mapped
    const mappedHeaders = Object.keys(this.fieldMapping);

    // For each mapped CSV header
    for (const csvHeader of mappedHeaders) {
      const dbField = this.fieldMapping[csvHeader];

      // Find the position of this header in the CSV
      const headerIndex = this.csvHeaders.indexOf(csvHeader);

      // If header found and row has data at this position
      if (headerIndex >= 0 && headerIndex < row.length) {
        let value = row[headerIndex].trim();

        // Skip empty values
        if (!value) {
          continue;
        }

        // Handle special transformations
        if (dbField === 'code_uses_chemicals') {
          const lowerValue = value.toLowerCase();
          value = ['yes', 'y', '1'].includes(lowerValue) ? '1' : '0';
        }

        mapped[dbField] = value;
      }
    }

    // Skip if no code_name
    if (!mapped['code_name']) {
      return null;
    }

    mapped['_status'] = 'new'; // Frontend just shows 'new', server will determine actual status
    return mapped;
  }

  /**
   * Process the import - send CSV content and mapping to server
   */
  processImport(): void {
    if (!Object.values(this.fieldMapping).includes('code_name')) {
      this.mappingError = 'code_name field must be mapped';
      return;
    }

    this.isLoading = true;
    this.mappingError = '';

    // Prepare data to send to server
    const importData = {
      csv_content: this.csvContent,
      field_mapping: this.fieldMapping
    };

    this.codeService.processImport(importData).subscribe(
      (response: any) => {
        if (response && response.message) {
          this.importResults = response.message;
          this.uploadStep = 3;
        }
        this.isLoading = false;
      },
      (error: any) => {
        const errorMsg = error && error.error && error.error.message ? error.error.message : 'Error processing import';
        this.mappingError = errorMsg;
        this.isLoading = false;
      }
    );
  }

  /**
   * Reset and start over
   */
  resetImport(): void {
    this.uploadStep = 1;
    this.csvFile = null;
    this.csvFilename = '';
    this.csvContent = '';
    this.csvHeaders = [];
    this.csvRows = [];
    this.fieldMapping = {};
    this.suggestedMapping = {};
    this.mappedFields = [];
    this.previewData = [];
    this.importResults = { imported: 0, updated: 0, errors: [] };
    this.fileError = '';
    this.mappingError = '';
  }

  /**
   * Go back to previous step
   */
  goBack(): void {
    if (this.uploadStep > 1) {
      this.uploadStep--;
      this.mappingError = '';
    }
  }

  /**
   * Check if field is mapped to a database field
   */
  isMapped(csvField: string): boolean {
    return this.fieldMapping[csvField] !== undefined;
  }

  /**
   * Get mapped database field name
   */
  getMappedFieldName(csvField: string): string {
    return this.fieldMapping[csvField] || 'unmapped';
  }

  /**
   * Get display name for a database field
   */
  getDisplayName(dbField: string): string {
    return this.fieldDisplayNames[dbField] || dbField;
  }

  /**
   * Get list of mapped fields in order (CSV header -> DB field)
   */
  getMappedFields(): Array<{ csvField: string; dbField: string }> {
    const mapped: Array<{ csvField: string; dbField: string }> = [];

    // Iterate through CSV headers in order
    for (const csvHeader of this.csvHeaders) {
      if (this.fieldMapping[csvHeader]) {
        mapped.push({
          csvField: csvHeader,
          dbField: this.fieldMapping[csvHeader]
        });
      }
    }

    return mapped;
  }
}
